import { Badge } from '@/components/ui/badge'
import {
  createDefaultCalculationRules,
  createDefaultFormVariables,
  createDefaultOutcomeBranches,
} from '@/lib/form-calculation'
import type { Question } from '@/types/form'
import { Calculator, GitFork, Variable } from 'lucide-react'

export function VariablePanel({
  questions,
  showVariables,
  showCalculations,
  showOutcomes,
}: {
  questions: Question[]
  showVariables: boolean
  showCalculations: boolean
  showOutcomes: boolean
}) {
  const variables = createDefaultFormVariables()
  const calculations = createDefaultCalculationRules(questions)
  const outcomes = createDefaultOutcomeBranches()

  if (!showVariables && !showCalculations && !showOutcomes) return null

  return (
    <div className="space-y-3">
      {showVariables ? (
        <section className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="flex items-center gap-2 font-semibold text-slate-950">
            <Variable className="size-4 text-indigo-600" />
            變數 contract
          </div>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            這些是前端-only mock-safe 變數，用於邏輯測試器與結果頁分流預覽。
          </p>
          <div className="mt-3 space-y-2">
            {variables.map((variable) => (
              <div
                key={variable.id}
                className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs text-slate-700">{variable.key}</span>
                  <Badge variant="secondary">{variableLabel(variable.kind)}</Badge>
                </div>
                <div className="mt-1 text-sm font-medium text-slate-950">{variable.label}</div>
                {variable.description ? (
                  <div className="mt-1 text-xs leading-5 text-slate-500">
                    {variable.description}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {showCalculations ? (
        <section className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="flex items-center gap-2 font-semibold text-slate-950">
            <Calculator className="size-4 text-indigo-600" />
            計算規則
          </div>
          <div className="mt-3 space-y-2">
            {calculations.map((rule) => (
              <div key={rule.id} className="rounded-md border border-slate-200 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-950">{rule.label}</span>
                  <Badge variant="outline">{calculationModeLabel(rule.mode)}</Badge>
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  來源題目：{rule.sourceQuestionIds.length || 0} 題
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {showOutcomes ? (
        <section className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="flex items-center gap-2 font-semibold text-slate-950">
            <GitFork className="size-4 text-indigo-600" />
            結果頁分流草案
          </div>
          <div className="mt-3 space-y-2">
            {outcomes.map((outcome) => (
              <div key={outcome.id} className="rounded-md border border-slate-200 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-950">{outcome.label}</span>
                  <Badge variant={outcome.target === 'result_page' ? 'default' : 'secondary'}>
                    {outcome.target === 'result_page' ? '結果頁' : '完成頁'}
                  </Badge>
                </div>
                <div className="mt-1 text-xs leading-5 text-slate-500">
                  {outcome.description ?? '未設定說明'}
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  )
}

function variableLabel(kind: string) {
  const labels: Record<string, string> = {
    hidden: '隱藏',
    score: '分數',
    money: '金額',
    calculated: '計算',
  }
  return labels[kind] ?? kind
}

function calculationModeLabel(mode: string) {
  const labels: Record<string, string> = {
    sum: '加總',
    average: '平均',
    count: '計數',
    weighted: '加權',
    custom: '公式',
  }
  return labels[mode] ?? mode
}
